import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { startSession, endSession } from '../api/session';

import type { LoginPayload, SignupPayload, SignupResponse, TokenResponse, User } from '../api/authApi';
import { fetchCurrentUser, login as loginApi, signup as signupApi, verifyLoginMfa as verifyLoginMfaApi } from '../api/authApi';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  login: (payload: LoginPayload) => Promise<{ mfaRequired: boolean; email?: string }>;
  verifyLoginMfa: (email: string, otp: string) => Promise<void>;
  signup: (payload: SignupPayload) => Promise<SignupResponse>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshAccessToken: () => Promise<void>;
  clearAuthState: () => void;
  loginWithResponse: (data: TokenResponse) => void;
  loginWithToken: (accessToken: string, authTokensJson?: string, initialUser?: User | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const TOKEN_KEY = 'policyora_token';
const AUTH_META_KEY = 'auth_meta';

const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      console.warn('localStorage getItem blocked for key', key, err);
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (err) {
      console.warn('localStorage setItem blocked for key', key, err);
    }
  },
  removeItem: (key: string) => {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      console.warn('localStorage removeItem blocked for key', key, err);
    }
  },
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const storedToken = safeStorage.getItem(TOKEN_KEY);

    if (!storedToken) {
      setLoading(false);
      return;
    }

    setToken(storedToken);
    // Access tokens last 15 minutes; keep renewing from the refresh cookie so a
    // returning user is not signed out mid-session.
    startSession(storedToken);
    fetchCurrentUser(storedToken)
      .then((u) => {
        setUser(u);
      })
      .catch(async (err: unknown) => {
        // Only an authentication failure ends the session. A rate limit (429), a server blip (5xx) or a
        // network error is not "signed out" — on the live site a burst of page loads hit 429 and the user
        // was bounced to the login page with a valid token.
        const status = (err as { status?: number } | null)?.status;
        if (status !== undefined && status !== 401 && status !== 403) return;
        // Access token expired — try cookie-based refresh
        try {
          await refreshAccessToken();
          const newToken = safeStorage.getItem(TOKEN_KEY);
          if (newToken) {
            const newUser = await fetchCurrentUser(newToken);
            setUser(newUser);
          }
        } catch {
          setToken(null);
          setRefreshToken(null);
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
    // This hydration should only run once on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stamp logout_at when the tab is closed so session duration metrics are
  // accurate even without an explicit logout click.
  // Uses sendBeacon (fire-and-forget, survives tab close) to a dedicated
  // /session-end endpoint that accepts the token in the body — sendBeacon
  // cannot send custom headers so the normal /logout endpoint won't work.
  useEffect(() => {
    const sendSessionEnd = () => {
      const currentToken = safeStorage.getItem(TOKEN_KEY);
      if (!currentToken) return;
      const baseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
      const fd = new FormData();
      fd.append('access_token', currentToken);
      navigator.sendBeacon(`${baseUrl}/api/v1/auth/session-end`, fd);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendSessionEnd();
    };

    window.addEventListener('pagehide', sendSessionEnd);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', sendSessionEnd);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const getStoredIsAdmin = (): boolean => {
    try {
      const raw = safeStorage.getItem(AUTH_META_KEY);
      if (!raw) return false;
      return Boolean(JSON.parse(raw)?.is_admin);
    } catch {
      return false;
    }
  };

  const handleAuthSuccess = useCallback((data: TokenResponse, isAdminOverride?: boolean) => {
    const isAdmin = isAdminOverride ?? getStoredIsAdmin();
    setToken(data.access_token);
    setUser(data.user);
    safeStorage.setItem(TOKEN_KEY, data.access_token);
    startSession(data.access_token);
    // Store non-sensitive metadata only — no tokens
    safeStorage.setItem(AUTH_META_KEY, JSON.stringify({
      username: data.cognito_username || data.user.email,
      email: data.user.email,
      is_admin: isAdmin,
    }));
    // refresh_token and id_token are NOT stored in localStorage;
    // the refresh_token lives in an httpOnly cookie set by the server
  }, []);

  const login = async (payload: LoginPayload): Promise<{ mfaRequired: boolean; email?: string }> => {
    const res = await loginApi(payload);
    // With login MFA on, the server returns a challenge instead of a session.
    if (res && (res as { mfa_required?: boolean }).mfa_required) {
      return { mfaRequired: true, email: (res as { email: string }).email };
    }
    handleAuthSuccess(res as TokenResponse, false);
    return { mfaRequired: false };
  };

  const verifyLoginMfa = async (email: string, otp: string) => {
    const res = await verifyLoginMfaApi({ email, otp });
    handleAuthSuccess(res, false);
  };

  const signup = async (payload: SignupPayload): Promise<SignupResponse> => {
    return signupApi(payload);
  };

  const clearAuthState = () => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    endSession();
    safeStorage.removeItem(TOKEN_KEY);
    safeStorage.removeItem(AUTH_META_KEY);
  };

  const logout = () => {
    const currentToken = safeStorage.getItem(TOKEN_KEY);
    const baseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
    if (currentToken) {
      fetch(`${baseUrl}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    clearAuthState();
  };

  const refreshAccessToken = useCallback(async () => {
    const meta = safeStorage.getItem(AUTH_META_KEY);
    const username = meta ? (JSON.parse(meta)?.username ?? null) : null;

    const formData = new FormData();
    if (username) {
      formData.append('username', username);
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
    const response = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',  // sends the httpOnly refresh_token cookie
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data: TokenResponse = await response.json();
    handleAuthSuccess(data);
  }, [handleAuthSuccess]);

  const updateUser = (next: User) => {
    setUser(next);
  };

  const loginWithResponse = (data: TokenResponse) => {
    handleAuthSuccess(data);
  };

  const loginWithToken = async (accessToken: string, authTokensJson?: string, initialUser?: User | null) => {
    safeStorage.setItem(TOKEN_KEY, accessToken);
    if (authTokensJson) {
      try {
        const parsed = JSON.parse(authTokensJson);
        // Persist non-sensitive metadata only
        safeStorage.setItem(AUTH_META_KEY, JSON.stringify({
          username: parsed.username ?? parsed.email ?? '',
          email: parsed.email ?? parsed.username ?? '',
          is_admin: parsed.is_admin ?? false,
        }));
      } catch { /* ignore */ }
    }
    setToken(accessToken);

    if (initialUser) {
      setUser(initialUser);
      return;
    }

    try {
      const u = await fetchCurrentUser(accessToken);
      setUser(u);
    } catch {
      setUser(null);
    }
  };

  const value: AuthContextValue = {
    user,
    token,
    refreshToken,
    loading,
    login,
    verifyLoginMfa,
    signup,
    logout,
    updateUser,
    refreshAccessToken,
    clearAuthState,
    loginWithResponse,
    loginWithToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

